import { useState, useEffect, useRef } from "react";
import { createClient } from "@supabase/supabase-js";

const SUPABASE_URL = "https://hqarozilsolrobyufwrp.supabase.co";
const SUPABASE_ANON = "sb_publishable_STZ3J7jxJ4AFXEgWjrFQZw_WnLQxAWz";

const supabase = createClient(SUPABASE_URL, SUPABASE_ANON);

export default function Chatbot() {
  const [bookContent, setBookContent] = useState("");
  const [config, setConfig] = useState({
    bot_name: "Umrah Assistant",
    welcome_message: "Assalamu Alaikum! I am your Hajj & Umrah travel assistant. I will help you plan your blessed journey. Let's get started!",
    primary_color: "#1a6b3c",
    placeholder: "Type your message...",
  });
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [fetchingDB, setFetchingDB] = useState(true);
  const [leadSaved, setLeadSaved] = useState(false);
  const endRef = useRef();
  const inputRef = useRef();

  useEffect(() => {
    const init = async () => {
      setFetchingDB(true);
      try {
        const [contentRes, settingsRes] = await Promise.all([
          supabase.from("chatbot_content").select("title, content").eq("is_active", true).limit(1).single(),
          supabase.from("chatbot_settings").select("*").limit(1).single(),
        ]);
        if (contentRes.data) setBookContent(contentRes.data.content);
        if (settingsRes.data) setConfig(settingsRes.data);
      } catch (e) {}
      setFetchingDB(false);
    };
    init();
  }, []);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, loading]);

  const sendMessage = async (text) => {
    const userText = (text || input).trim();
    if (!userText || loading) return;
    setInput("");

    const newMessages = [...messages, { role: "user", content: userText }];
    setMessages(newMessages);
    setLoading(true);

    try {
      const res = await fetch("https://api.anthropic.com/v1/messages", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-api-key":            "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",

          "anthropic-version": "2023-06-01",
          "anthropic-dangerous-direct-browser-access": "true",
        },
        body: JSON.stringify({
          model: "claude-sonnet-4-20250514",
          max_tokens: 1000,
          system: `You are a friendly Umrah & Hajj travel assistant for HAQ Travels UK.
Today's date is ${new Date().toDateString()}.

════════════════════════════════════
CORE RULES — NEVER BREAK THESE
════════════════════════════════════
1. Ask ONLY ONE question at a time. Never two.
2. NEVER ask for information the customer already gave in ANY previous message. Read the full conversation carefully before asking anything.
3. If the customer already told you their name, passengers, kids info, or any detail — skip that step completely.
4. If a customer says they don't want to give their phone number, say "No problem!" and move on to ask for email instead. Never ask for phone again.
5. If a customer says they don't want to give email either, move on. Never ask again.
6. Be warm and natural. Use "InshaAllah", "Alhamdulillah", "MashaAllah" naturally.
7. NEVER say "according to the document". Talk naturally.

════════════════════════════════════
GREETING RULE
════════════════════════════════════
If the visitor says ONLY a greeting (hi, hello, salam, hey, assalam) with no other info:
Reply EXACTLY: "Wa Alaikum Assalam! Welcome to HAQ Travels 🕋 Alhamdulillah, we are here to help you plan your blessed Umrah journey. How may I help you today?"

If the visitor's first message contains info (e.g. "Hi my name is Ali" or "Hi I want Umrah for 4 people"):
Acknowledge the info they gave and continue from there — do NOT ignore it.

════════════════════════════════════
INFO COLLECTION — EXACT ORDER
════════════════════════════════════
Collect these in order, SKIPPING any already provided in the conversation:

STEP 1 — TRAVEL MONTH:
Ask: "In which month and year are you planning to travel for Umrah InshaAllah?"

If customer says they want the CHEAPEST month, use the CHEAP MONTHS GUIDE below to advise them, then ask: "Would any of these months work for you?"

STEP 2 — FLEXIBLE DATES:
Ask: "JazakAllah! Do you have specific dates in mind, or are your dates flexible?"

STEP 3 — DEPARTURE AIRPORT:
Ask: "Which city or airport will you be departing from? (e.g. London Heathrow, Manchester, Birmingham)"

STEP 4 — PASSENGERS:
Ask: "How many passengers will be travelling in total?"

STEP 5 — KIDS & INFANTS:
Ask: "Will there be any children or infants travelling?
• Infants: under 2 years old
• Children: 2–11 years old
• Adults: 12 years and above
If yes, please mention how many and their ages InshaAllah."

If customer already gave full breakdown like "4 persons, 2 infants, 1 child, 1 adult" — accept it as-is, DO NOT ask again.

STEP 6 — STAY DURATION:
Ask: "How long would you like to stay? For example: 7 nights, 10 nights, 14 nights, or 21 nights?"

STEP 7 — HOTEL TYPE:
Ask: "What type of hotel would you prefer near the Haram?
⭐⭐⭐⭐⭐ 5-Star (luxury, steps from Haram)
⭐⭐⭐⭐ 4-Star (comfortable, short walk)
⭐⭐⭐ 3-Star (good value)
🏨 Budget (most affordable)"

STEP 8 — PASSPORT / NATIONALITY:
Ask: "What nationality passport will you be travelling on? (e.g. British, Pakistani, Bangladeshi)"

STEP 9 — FULL NAME:
Ask: "To prepare your personalised quote, could I please have your full name?"
(Skip if already given)

STEP 10 — PHONE NUMBER:
Ask: "And your best contact number please?"
If they decline or say they don't want to share: say "No problem at all!" and go to Step 11.
NEVER ask for phone again after they decline.

STEP 11 — EMAIL:
Ask: "Could I also take your email address so we can send the full package details InshaAllah?"
If they decline: say "No problem!" and proceed to save lead anyway.
NEVER ask for email again after they decline.

════════════════════════════════════
CHEAP MONTHS GUIDE (Gregorian Calendar)
════════════════════════════════════
Use this to advise customers who ask for cheap months.

IMPORTANT RAMADAN RULE:
- Ramadan 2026: starts approx 18 Feb 2026 (30 days, ends ~19 Mar 2026)
- Ramadan 2027: starts approx 8 Feb 2027 (30 days, ends ~9 Mar 2027)
- Last 10 days of Ramadan = EXTREMELY EXPENSIVE (avoid)
- All of Ramadan = expensive, especially last 10 days
- When Ramadan falls in a month, shift the cheap window AWAY from Ramadan days.
  Formula: cheap window = days in that month that are NOT in Ramadan or its last 10 days.

BASE CHEAP WINDOWS (adjust for Ramadan each year):
- January:   After 10th is cheap
- February:  First two weeks cheap (adjust if Ramadan starts in Feb)
- March:     After Eid ul Fitr is cheap (Eid = end of Ramadan + 1 day)
- April:     After 15th is cheap
- May:       After 20th is cheap
- June:      Entire month cheap
- July:      First 20 days cheap
- August:    Entire month EXPENSIVE (avoid)
- September: Entire month cheap
- October:   First two weeks cheap
- November:  Entire month cheap
- December:  First three weeks cheap

FOR 2026 SPECIFICALLY (Ramadan 18 Feb – 19 Mar 2026):
- February 2026: Only first 17 days cheap (Ramadan starts 18 Feb)
- March 2026: After ~20 Mar cheap (after Eid ul Fitr)
- Cheapest months 2026: June, September, November

FOR 2027 SPECIFICALLY (Ramadan 8 Feb – 9 Mar 2027):
- February 2027: Only first 7 days cheap (Ramadan starts 8 Feb)
- March 2027: After ~10 Mar cheap (after Eid ul Fitr)
- Cheapest months 2027: June, September, November (same pattern)

When advising, be specific: e.g. "For 2026, June, September and November are the cheapest months InshaAllah. August is best avoided as it is peak season."

════════════════════════════════════
AGENCY KNOWLEDGE
════════════════════════════════════
${bookContent || `HAQ Travels is a UK-based Hajj and Umrah travel agency.

PACKAGES:
- Economy from £699 per person
- Standard from £899 per person
- Premium 4-star from £1,199 per person
- Luxury 5-star from £1,799 per person
- Group discounts available
- Ramadan packages available (limited seats, book early)

HOTELS IN MAKKAH:
- 5-Star: Raffles Makkah, Fairmont Makkah, Swissotel Makkah (0-2 min to Haram)
- 4-Star: Hilton Suites, Marriott Hotel (5-10 min walk)
- 3-Star: Al Marwa Rayhaan, Anjum Hotel (10-15 min walk)
- Budget: Various hotels 500m-1km from Haram

HOTELS IN MADINAH:
- 5-Star: Anwar Al Madinah Movenpick, Marriott Madinah
- 4-Star: Dallah Taibah, Bab Al Qasr
- 3-Star/Budget: Multiple options near Masjid Nabawi

FLIGHTS: Direct from London Heathrow, Manchester, Birmingham
Airlines: Saudia, Emirates, Turkish Airlines, British Airways (~6-7 hrs)

VISA: UK passports processed in 3-5 working days. All nationalities handled.

INCLUDED: Return flights, visa, hotel in Makkah & Madinah, airport transfers, 24/7 support

PAYMENT: Deposit from £150. Balance due 6-8 weeks before travel. Flexible plans available.`}

════════════════════════════════════
SAVE LEAD — WHEN ALL INFO COLLECTED
════════════════════════════════════
Once you have: travel month, flexible dates, departure, passengers, kids/infants, duration, hotel type, passport, name, and contact (phone or email or both) —
Add this HIDDEN line at the very end of your message (user cannot see it):
SAVE_LEAD:{"name":"value","phone":"value","email":"value","passengers":"value","travel_dates":"value","flexible_dates":"value","passport_type":"value","departure_airport":"value","stay_duration":"value","property_type":"value","kids_infants":"value","extra_notes":"value"}

Only output SAVE_LEAD once. Then say:
"JazakAllah Khair! Alhamdulillah, we have everything we need. Our HAQ Travels team will be in touch within 24 hours with your personalised Umrah package quote InshaAllah. May Allah accept your Umrah. Ameen! 🤲

Is there anything else I can help you with?"`,
          messages: newMessages.map((m) => ({ role: m.role, content: m.content })),
        }),
      });

      const data = await res.json();
      let reply = data.content?.[0]?.text || "Sorry, I could not generate a response.";

      if (reply.includes("SAVE_LEAD:") && !leadSaved) {
        const match = reply.match(/SAVE_LEAD:(\{[\s\S]*?\})/);
        if (match) {
          try {
            const lead = JSON.parse(match[1]);
            const { error: dbErr } = await supabase.from("leads").insert([{
              name:              lead.name || "",
              phone:             lead.phone || "",
              email:             lead.email || "",
              passengers:        lead.passengers || "",
              travel_dates:      lead.travel_dates || "",
              flexible_dates:    lead.flexible_dates || "",
              passport_type:     lead.passport_type || "",
              departure_airport: lead.departure_airport || "",
              stay_duration:     lead.stay_duration || "",
              property_type:     lead.property_type || "",
              kids_infants:      lead.kids_infants || "",
              extra_notes:       lead.extra_notes || "",
            }]);
            if (!dbErr) setLeadSaved(true);
          } catch (e) { console.error("Lead save error", e); }
        }
        reply = reply.replace(/SAVE_LEAD:\{[\s\S]*?\}/, "").trim();
      }

      setMessages((prev) => [...prev, { role: "assistant", content: reply }]);
    } catch {
      setMessages((prev) => [...prev, { role: "assistant", content: "Network error. Please try again." }]);
    }
    setLoading(false);
  };

  const handleKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendMessage(); }
  };

  const C = config.primary_color || "#1a6b3c";

  const css = `
    @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600&display=swap');
    * { box-sizing:border-box; margin:0; padding:0; }
    .cw { display:flex; flex-direction:column; height:100vh; background:#f0f7f4; font-family:'Inter',sans-serif; }
    .ch { background:${C}; color:#fff; padding:1rem 1.5rem; display:flex; align-items:center; gap:12px; }
    .av { width:42px; height:42px; border-radius:50%; background:rgba(255,255,255,.2); display:flex; align-items:center; justify-content:center; font-size:1.3rem; flex-shrink:0; }
    .hi h2 { font-size:1rem; font-weight:600; }
    .hi p { font-size:.75rem; opacity:.8; margin-top:1px; }
    .sd { width:8px; height:8px; border-radius:50%; background:#6ef078; margin-left:auto; box-shadow:0 0 6px #6ef078; animation:pulse 2s infinite; }
    .saved { background:rgba(255,255,255,.15); border-radius:8px; padding:3px 10px; font-size:.72rem; margin-left:8px; }
    @keyframes pulse { 0%,100%{opacity:1} 50%{opacity:.5} }
    .ma { flex:1; overflow-y:auto; padding:1.5rem; display:flex; flex-direction:column; gap:1rem; }
    .mr { display:flex; }
    .mr.u { justify-content:flex-end; }
    .mr.b { justify-content:flex-start; }
    .bb { max-width:78%; padding:.75rem 1rem; border-radius:16px; font-size:.9rem; line-height:1.65; white-space:pre-wrap; word-break:break-word; }
    .bb.u { background:${C}; color:#fff; border-radius:16px 16px 4px 16px; }
    .bb.b { background:#fff; color:#1a2e1a; border-radius:16px 16px 16px 4px; border:1px solid #d0e8da; box-shadow:0 1px 4px rgba(0,0,0,.06); }
    .bb.w { background:#e4f2ea; color:#1a4a2a; border:1px solid #b8ddc8; }
    .tb { background:#fff; border:1px solid #d0e8da; border-radius:16px 16px 16px 4px; padding:.85rem 1.1rem; display:flex; gap:5px; align-items:center; }
    .da { width:7px; height:7px; border-radius:50%; background:#4a9a6a; animation:bounce 1.2s ease-in-out infinite; }
    .da:nth-child(2){animation-delay:.2s} .da:nth-child(3){animation-delay:.4s}
    @keyframes bounce { 0%,80%,100%{transform:translateY(0)} 40%{transform:translateY(-6px)} }
    .ia { background:#fff; border-top:1px solid #d0e8da; padding:1rem 1.5rem; display:flex; gap:10px; align-items:flex-end; }
    .mi { flex:1; border:1.5px solid #c0ddd0; border-radius:12px; padding:.7rem 1rem; font-size:.9rem; font-family:'Inter',sans-serif; resize:none; outline:none; min-height:44px; max-height:120px; transition:border-color .2s; color:#1a2e1a; }
    .mi:focus { border-color:${C}; }
    .mi::placeholder { color:#aaa; }
    .sb { background:${C}; border:none; border-radius:12px; width:44px; height:44px; display:flex; align-items:center; justify-content:center; cursor:pointer; transition:opacity .2s,transform .1s; flex-shrink:0; }
    .sb:hover{opacity:.9} .sb:active{transform:scale(.95)} .sb:disabled{opacity:.4;cursor:not-allowed}
    .si { width:18px; height:18px; fill:none; stroke:#fff; stroke-width:2; stroke-linecap:round; stroke-linejoin:round; }
    .sq { display:flex; flex-wrap:wrap; gap:8px; margin-top:.5rem; }
    .sq button { background:#e4f2ea; border:1px solid #b8ddc8; border-radius:20px; padding:.4rem .9rem; font-size:.78rem; color:#1a6b3c; cursor:pointer; font-family:'Inter',sans-serif; transition:background .2s; }
    .sq button:hover { background:#cce8d8; }
    .ls { display:flex; align-items:center; justify-content:center; flex:1; flex-direction:column; gap:.75rem; color:${C}; font-size:.95rem; }
  `;

  if (fetchingDB) return (
    <>
      <style>{css}</style>
      <div className="cw">
        <div className="ch"><div className="av">🕌</div><div className="hi"><h2>Umrah Assistant</h2><p>Loading...</p></div></div>
        <div className="ls"><div style={{fontSize:"2.5rem"}}>🕋</div><span>Setting up your assistant...</span></div>
      </div>
    </>
  );

  return (
    <>
      <style>{css}</style>
      <div className="cw">
        <div className="ch">
          <div className="av">🕌</div>
          <div className="hi">
            <h2>{config.bot_name || "Umrah Assistant"}</h2>
            <p>Hajj & Umrah Travel Expert</p>
          </div>
          {leadSaved && <span className="saved">✓ Details Saved</span>}
          <div className="sd" />
        </div>

        <div className="ma">
          <div className="mr b">
            <div className="bb b w">{config.welcome_message}</div>
          </div>

          {messages.length === 0 && !loading && (
            <div className="mr b">
              <div>
                <p style={{fontSize:".78rem",color:"#888",marginBottom:"6px"}}>Quick options:</p>
                <div className="sq">
                  {["I want to plan Umrah 🕋","Hajj packages?","Hotel options near Haram","How much does Umrah cost?"].map(q => (
                    <button key={q} onClick={() => sendMessage(q)}>{q}</button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {messages.map((m, i) => (
            <div key={i} className={`mr ${m.role === "user" ? "u" : "b"}`}>
              <div className={`bb ${m.role === "user" ? "u" : "b"}`}>{m.content}</div>
            </div>
          ))}

          {loading && (
            <div className="mr b">
              <div className="tb"><div className="da"/><div className="da"/><div className="da"/></div>
            </div>
          )}
          <div ref={endRef} />
        </div>

        <div className="ia">
          <textarea
            ref={inputRef}
            className="mi"
            placeholder={config.placeholder || "Type your message..."}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKey}
            rows={1}
            disabled={loading}
          />
          <button className="sb" onClick={() => sendMessage()} disabled={!input.trim() || loading}>
            <svg className="si" viewBox="0 0 24 24">
              <line x1="22" y1="2" x2="11" y2="13"/>
              <polygon points="22 2 15 22 11 13 2 9 22 2"/>
            </svg>
          </button>
        </div>
      </div>
    </>
  );
}